import { useEffect, useRef } from "react";

export interface HtmlWithScriptsProps {
  html: string;
}

export default function HtmlWithScripts(props: HtmlWithScriptsProps) {
  const { html } = props;

  const containerRef = useRef<HTMLDivElement>(null!);
  const hasProcessedRef = useRef(false);
  const contentHashRef = useRef("");

  useEffect(() => {
    if (hasProcessedRef.current && contentHashRef.current === html) return;

    contentHashRef.current = html;
    containerRef.current.innerHTML = "";

    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = html;

    Array.from(tempContainer.childNodes).forEach((node) => {
      if (!(node instanceof HTMLScriptElement)) {
        containerRef.current?.appendChild(node.cloneNode(true));
      }
    });

    const externalScripts: HTMLScriptElement[] = [];
    const inlineScripts: HTMLScriptElement[] = [];

    tempContainer.querySelectorAll("script").forEach((script) => {
      if (script.src) {
        externalScripts.push(script);
      } else {
        inlineScripts.push(script);
      }
    });

    const loadScripts = async () => {
      for (const oldScript of externalScripts) {
        await new Promise<void>((resolve, reject) => {
          const newScript = document.createElement("script");

          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });

          newScript.onload = () => resolve();
          newScript.onerror = () =>
            reject(new Error(`Failed to load script: ${newScript.src}`));

          containerRef.current?.appendChild(newScript);
        });
      }

      for (const oldScript of inlineScripts) {
        const newScript = document.createElement("script");

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        newScript.appendChild(document.createTextNode(oldScript.innerHTML));

        containerRef.current?.appendChild(newScript);
      }
    };

    loadScripts().catch((err) => console.error("Error loading scripts:", err));

    hasProcessedRef.current = true;
  }, [html]);

  return <div ref={containerRef} />;
}
